import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getStockTakesApi, createStockTakeApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge } from '../../constants';
import PageHeader from 'components/PageHeader';
import ListShell from 'components/ListShell';
import DateField from 'components/Form/dateField';
import useStickyIdentity from '../../components/useStickyIdentity';

const StockTakeList = () => {
  const navigate = useNavigate();
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [conductedDate, setConductedDate] = useState('');
  const [createNotes, setCreateNotes] = useState('');

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = { page: pageSettings.page, page_size: pageSettings.pageSize };
    if (statusFilter) params.status = statusFilter;
    getStockTakesApi(params).then(response => {
      setData(response.data?.results || []);
      setPageSettings(prev => ({ ...prev, total: response.data?.count || 0 }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [pageSettings.page, pageSettings.pageSize, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const handleCreate = () => {
    if (!selectedWarehouse || !conductedDate) {
      toast.error('Warehouse and date are required', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setCreateLoading(true);
    createStockTakeApi({
      warehouse: selectedWarehouse.value,
      conducted_date: conductedDate,
      notes: createNotes
    }).then(response => {
      toast.success('Stock take created', { position: "top-right", autoClose: 3000, transition: Bounce });
      setCreateLoading(false);
      setShowCreateModal(false);
      const newId = response.data?.data?.id;
      if (newId) navigate(`/uniform-management/stock-take/${newId}`);
      else fetchData();
    }).catch(e => {
      setCreateLoading(false);
      const data = e.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        (data && typeof data === 'object'
          ? Object.entries(data)
              .filter(([k]) => k !== 'status')
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join(' | ')
          : null) ||
        'Failed to create stock take';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const pinLeft = useStickyIdentity();

  const columns = [
    {
      // Pinned: the warehouse is the row's identity on a stock take.
      title: 'Warehouse', dataIndex: 'warehouse_name', ...pinLeft(200),
      render: (text, record) => (
        <button type="button" className="table-link"
          onClick={() => navigate(`/uniform-management/stock-take/${record.id}`)}>{text}</button>
      )
    },
    { title: 'Conducted By', dataIndex: 'conducted_by_name' },
    { title: 'Date', dataIndex: 'conducted_date', render: (d) => formatDateHandler(d) },
    { title: 'Items', dataIndex: 'item_count' },
    {
      title: 'Total Variance', dataIndex: 'total_variance',
      render: (val) => (
        <span style={{ color: val < 0 ? '#ff4d4f' : val > 0 ? '#52c41a' : 'inherit', fontWeight: val !== 0 ? 600 : 400 }}>
          {val > 0 ? `+${val}` : val}
        </span>
      )
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <span style={getStatusBadge(status)}>{status.replace('_', ' ')}</span>
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button label="View" icon="fa fa-eye" className="btn btn-sm btn-outline-primary"
          onClick={() => navigate(`/uniform-management/stock-take/${record.id}`)} />
      )
    }
  ];

  const filterItems = [
    { key: '', label: 'All' },
    { key: 'IN_PROGRESS', label: 'In Progress' },
    { key: 'COMPLETED', label: 'Completed' },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title="Stock Takes"
          actions={
            <Button label="New Stock Take" icon="fa fa-plus" className="btn btn-primary btn-sm"
              onClick={() => { setSelectedWarehouse(null); setConductedDate(''); setCreateNotes(''); setShowCreateModal(true); }} />
          }
        />

        <ListShell
          filters={filterItems}
          activeKey={statusFilter}
          onFilterChange={(key) => { setStatusFilter(key); setPageSettings(prev => ({ ...prev, page: 1 })); }}
          resultCount={pageSettings.total}
          loading={loading}
        >
          <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
            pagination={{
              total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
              pageSizeOptions, itemRender, showSizeChanger: true
            }}
            onChange={handleChange} scroll={{ x: "max-content" }} />
        </ListShell>

        <Modal
          title={
            <div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Create Stock Take</div>
              <div style={{ fontSize: 13, fontWeight: 400, color: '#6B7280', marginTop: 4 }}>
                Snapshot a warehouse's physical stock to reconcile against system records.
              </div>
            </div>
          }
          open={showCreateModal}
          onCancel={() => setShowCreateModal(false)}
          width={520}
          centered
          destroyOnClose
          footer={[
            <button key="cancel" type="button" className="btn btn-light"
              onClick={() => setShowCreateModal(false)} disabled={createLoading}>
              Cancel
            </button>,
            <Button
              key="submit"
              label="Create Stock Take"
              icon="fa fa-clipboard-check"
              className="btn btn-primary ms-2"
              loading={createLoading}
              disabled={createLoading || !selectedWarehouse || !conductedDate}
              onClick={handleCreate}
            />
          ]}
        >
          <div className="mb-3">
            <label className="col-form-label">Warehouse <span className="text-danger">*</span></label>
            <FilterSelect isClearable onChange={setSelectedWarehouse} optionLabel="label" optionValue="value"
              options={warehouseOptions} value={selectedWarehouse}
              placeholder="Select warehouse to count" isLoading={warehousesLoading} />
          </div>
          <div className="mb-3">
            <label className="col-form-label" htmlFor="stock-take-conducted-date">Conducted Date <span className="text-danger">*</span></label>
            <DateField id="stock-take-conducted-date" value={conductedDate} onChange={setConductedDate} />
          </div>
          <div className="mb-1">
            <label className="col-form-label">Notes <small className="text-muted">(optional)</small></label>
            <textarea className="form-control" rows={3} value={createNotes}
              onChange={(e) => setCreateNotes(e.target.value)}
              placeholder="Reason for stock take, special instructions, etc." />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default StockTakeList;
